import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Route,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Observable } from 'rxjs';
import { AgencyPrioritiesComponent } from './admin/agency-priorities/agency-priorities.component';
import { AssignmentDefaultsComponent } from './admin/assignmentDefaults/assignment-defaults.component';
import { CancelReasonsComponent } from './admin/cancelReasons/cancel-reasons.component';
import { CertificateGroupsComponent } from './admin/certificate-groups/certificate-groups.component';
import { CustomCriteriaComponent } from './admin/custom-criteria/custom-criteria.component';
import { CustomerFlagsComponent } from './admin/customerFlags/customer-flags.component';
import { DemandReasonsComponent } from './admin/demandReasons/demand-reasons.component';
import { JobRequestApprovalComponent } from './admin/jobRequestApproval/job-request-approval.component';
import { PerigonMappingComponent } from './admin/perigonMapping/perigon-mapping.component';
import { PoolerTypesComponent } from './admin/poolerType/pooler-types.component';
import { SharedPoolSourceSettingsComponent } from './admin/shared-pool-source-settings/shared-pool-source-settings.component';
import { TariffsComponent } from './admin/tariffs/tariffs.component';
import { AdminReportsComponent } from './admin/reports/admin-reports.component';
import { ConditionalTransfersComponent } from './admin/transfer-conditions/conditional-transfers.component';
import { AvailabilityManagement } from './customers/availability-management/availability-management.component';
import { CustomerUsersComponent } from './customers/customer-users/customer-users.component';
import { EmploymentEditComponent } from './employments/employment-edit/employment-edit.component';
import { AddEmploymentInviteeComponent } from './employments/employment-invitee-form/add-employment-invitee.component';
import { EmploymentsIndexComponent } from './employments/employments-index/employments-index.component';
import { PoolerEmploymentsIndexComponent } from './employments/pooler-employments-index/pooler-employments-index.component';
import { JobMatrixComponent } from './job-matrix/job-matrix.component';
import { AddInviteeComponent } from './jobs/invitee-form/add-invitee.component';
import { JobEditComponent } from './jobs/job-edit/job-edit.component';
import { JobMultidayEditComponent } from './jobs/job-multiday-edit/job-multiday-edit.component';
import { JobRequestEditComponent } from './jobs/job-request-edit/job-request-edit.component';
import { JobRequestsComponent } from './jobs/job-requests/job-requests.component';
import { JobsIndexComponent } from './jobs/jobs-index/jobs-index.component';
import { TimeTrackingFormComponent } from './jobs/time-tracking-form/time-tracking-form.component';
import { TranscribeJobListComponent } from './jobs/transcribe-job-list/transcribe-job-list.component';
import { OrganisationIndexComponent } from './organisations/organisation-index';
import { PlanningMatrixComponent } from './planningMatrix/planning-matrix.component';
import { ReportsIndexComponent } from './reports/reports-index';
import { UnsavedChangesGuard } from './shared/guards/unsaved-changes.guard';
import { PageNotFoundComponent } from './shared/page-not-found/page-not-found.component';
import { SupportComponent } from './shared/support/support.component';
import { SuperAdminDemoSettingsComponent } from './super-admin/demo-settings/demo-settings.component';
import { SuperAdminSettingsComponent } from './super-admin/super-admin-settings/super-admin-settings.component';
import { EmploymentAvailabilityEditComponent } from './temp-work/availability-edit/availability-edit.component';
import { EmploymentAvailabilityManagement } from './temp-work/availability-management/availability-management.component';
import { AvailabilityEditComponent } from './users/availability-edit/availability-edit.component';
import { CompleteProfileComponent } from './users/complete-profile/complete-profile.component';
import { ConfirmComponent } from './users/confirm/confirm.component';
import { UsersDashboardComponent } from './users/dashboard/dashboard.component';
import { ForgotPasswordFormComponent } from './users/forgot-password-form/forgot-password-form.component';
import { LoginFormComponent } from './users/login-form/login-form.component';
import { LogoutComponent } from './users/logout/logout.component';
import { ProfileEditComponent } from './users/profile-edit/profile-edit.component';
import { RegisterDemoComponent } from './users/register-demo/register-demo.component';
import { RegisterComponent } from './users/register/register.component';
import { ResetPasswordFormComponent } from './users/reset-password-form/reset-password-form.component';
import { TwoFactorAuthenticationComponent } from './users/two-factor-authentication/two-factor-authentication.component';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ):
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    if (!Meteor.userId()) {
      this.router
        .navigate(['/login'], { queryParams: { return: state.url } })
        .then();
      return false;
    }
    return true;
  }
}

export const routes: Route[] = [
  {
    path: '',
    redirectTo: 'login',
    pathMatch: 'full',
  },
  {
    path: 'login',
    component: LoginFormComponent,
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordFormComponent,
  },
  {
    path: '2fa',
    component: TwoFactorAuthenticationComponent,
  },
  {
    path: 'reset-password/:token',
    component: ResetPasswordFormComponent,
  },
  {
    path: 'enroll-account/:token',
    component: ResetPasswordFormComponent,
  },
  {
    path: 'register',
    component: RegisterComponent,
  },
  {
    path: 'register-demo',
    component: RegisterDemoComponent,
  },
  {
    path: 'confirm/:token',
    component: ConfirmComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'dashboard',
    component: UsersDashboardComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'customer/users',
    component: CustomerUsersComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'jobs',
    component: JobsIndexComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'jobs/:jobId',
    component: JobEditComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UnsavedChangesGuard],
  },
  {
    path: 'multiday-job/:multiDayId',
    component: JobMultidayEditComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UnsavedChangesGuard],
  },
  {
    path: 'jobs/:jobId/invitees/:secret',
    component: AddInviteeComponent,
  },
  {
    path: 'jobs/:jobId/time-tracking/:secret',
    component: TimeTrackingFormComponent,
  },
  {
    path: 'profile',
    component: ProfileEditComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UnsavedChangesGuard],
  },
  {
    path: 'welcome',
    component: CompleteProfileComponent,
  },
  {
    path: 'temp-work/availability',
    component: EmploymentAvailabilityEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'availability',
    component: AvailabilityEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'customer/availability-management',
    component: AvailabilityManagement,
    canActivate: [AuthGuard],
  },
  {
    path: 'logout',
    component: LogoutComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'support',
    component: SupportComponent,
  },
  {
    path: 'reports',
    component: ReportsIndexComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/organisations',
    component: OrganisationIndexComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'temp-work/availability-management',
    component: EmploymentAvailabilityManagement,
    canActivate: [AuthGuard],
  },
  {
    path: 'temp-work/employments',
    component: EmploymentsIndexComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'temp-work/pooler-employments',
    component: PoolerEmploymentsIndexComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'temp-work/employments/:employmentId',
    component: EmploymentEditComponent,
    canActivate: [AuthGuard],
    canDeactivate: [UnsavedChangesGuard],
  },
  {
    path: 'temp-work/employments/:employmentId/invitees/:secret',
    component: AddEmploymentInviteeComponent,
  },
  {
    path: 'admin/tariffs',
    component: TariffsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/job-defaults',
    component: AssignmentDefaultsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'job-requests',
    component: JobRequestsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'job-requests/:jobRequestId',
    component: JobRequestEditComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/customer-flags',
    component: CustomerFlagsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/pooler-types',
    component: PoolerTypesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'planning-matrix',
    component: PlanningMatrixComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/demand-reasons',
    component: DemandReasonsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/job-requests',
    component: JobRequestApprovalComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'transcribe-job-list',
    component: TranscribeJobListComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/cancel-reasons',
    component: CancelReasonsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/conditional-transfers',
    component: ConditionalTransfersComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/agency-priorities',
    component: AgencyPrioritiesComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/shared-pool-source-settings',
    component: SharedPoolSourceSettingsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'job-matrix',
    component: JobMatrixComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/certificate-groups',
    component: CertificateGroupsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/custom-criteria',
    component: CustomCriteriaComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/perigon-mapping',
    component: PerigonMappingComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'admin/reports',
    component: AdminReportsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'super-admin',
    component: SuperAdminSettingsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'super-admin/settings',
    component: SuperAdminSettingsComponent,
    canActivate: [AuthGuard],
  },
  {
    path: 'super-admin/demo-settings',
    component: SuperAdminDemoSettingsComponent,
    canActivate: [AuthGuard],
  },
  // 404 Page
  {
    path: '**',
    component: PageNotFoundComponent,
  },
];
